import { Container, SettingContainer } from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import {
  getAlarm,
  TAlarm,
  updateMeetingReminder,
  updateMeetingReminderTime,
  updateNoShowProcessing,
  updateNoShowWarning,
  updateNoShowWarningTime,
} from "apis/meeting-settings/alarm";
import { useEffect, useState } from "react";
import { useToast } from "hook/useToast";
import {
  NoshowNotificationSetting,
  TodayRoomNotificationSetting,
  PreMeetingNotificationSetting,
} from "uis/PolicyManage";
import { GLOBAL_MEETING_SETTING_VALUE } from "types/meeting-settings.types";
import { getMeetingRoomPolicyDetail } from "apis/meeting-settings/meeting-room";

const SettingAlarm = () => {
  const { showToast } = useToast();
  const [hasMeetingRoomUsedTablet, setHasMeetingRoomUsedTablet] = useState<boolean>(false);
  const [noShowEnabled, setNoShowEnabled] = useState<GLOBAL_MEETING_SETTING_VALUE>(GLOBAL_MEETING_SETTING_VALUE.OFF);
  const [noShowProcessingTime, setNoShowProcessingTime] = useState<number>(10);
  const [noShowWarningActive, setNoShowWarningActive] = useState<boolean>(false);
  const [noShowWarningTime, setNoShowWarningTime] = useState<number>(10);
  const [noShowProcessingActive, setNoShowProcessingActive] = useState<boolean>(false);
  const [meetingReminderActive, setMeetingReminderActive] = useState<boolean>(false);
  const [meetingReminderTime, setMeetingReminderTime] = useState<number>(10);
  const [alarmSettingData, setAlarmSettingData] = useState<TAlarm>();

  const toggleNoShowWarning = async (isNoShowWarningEnabled: boolean) => {
    const res = await updateNoShowWarning(isNoShowWarningEnabled);
    if (res.success) {
      showToast(res.message, "success");
      setNoShowWarningActive(isNoShowWarningEnabled);
    } else {
      showToast(res.message, "error");
    }
  };

  //* 노쇼 처리 시간 조회 (가장 낮은 처리 시간 값 반환)
  const getNoShowProcessingTime = async () => {
    const res = await getMeetingRoomPolicyDetail();
    if (res.success && res.data) {
      const minTime = Math.min(...res.data.map((room) => (room["noShowProcessingTime"] as number) ?? 100));
      setNoShowProcessingTime(minTime);
    }
  };

  const selectNoShowWarningTime = async (time: number) => {
    if (time >= noShowProcessingTime) {
      showToast(
        "노쇼 취소 처리 시간(현재 설정: " + noShowProcessingTime + "분)보다 작은 값으로만 설정 가능합니다.",
        "warning"
      );
    } else {
      const res = await updateNoShowWarningTime(time);
      if (res.success) {
        setNoShowWarningTime(time);
        showToast(res.message, "success");
      } else {
        showToast(res.message, "error");
      }
    }
  };

  const toggleNoShowProcessing = async (isNoShowProcessingEnabled: boolean) => {
    const res = await updateNoShowProcessing(isNoShowProcessingEnabled);
    if (res.success) {
      showToast(res.message, "success");
      setNoShowProcessingActive(isNoShowProcessingEnabled);
    } else {
      showToast(res.message, "error");
    }
  };

  const toggleMeetingReminder = async (isMeetingReminderEnabled: boolean) => {
    const res = await updateMeetingReminder(isMeetingReminderEnabled);
    if (res.success) {
      showToast(res.message, "success");
      setMeetingReminderActive(isMeetingReminderEnabled);
    } else {
      showToast(res.message, "error");
    }
  };

  const selectMeetingReminderTime = async (time: number) => {
    const res = await updateMeetingReminderTime(time);
    if (res.success) {
      setMeetingReminderTime(time);
      showToast(res.message, "success");
    } else {
      showToast(res.message, "error");
    }
  };

  const setAlarmData = () => {
    getAlarm().then((res) => {
      if (res.data) {
        setNoShowEnabled(res.data.isNoShowEnabled);
        setNoShowProcessingTime(res.data.noShowProcessingTime);
        setNoShowWarningActive(res.data.isNoShowWarningAlarmEnabled);
        setNoShowWarningTime(res.data.noShowWarningAlarmTime);
        setNoShowProcessingActive(res.data.isNoShowProcessingAlarmEnabled);
        setMeetingReminderActive(res.data.isReminderAlarmEnabled);
        setMeetingReminderTime(res.data.reminderAlarmTime);
        setHasMeetingRoomUsedTablet(res.data.hasMeetingRoomUsedTablet);
        setAlarmSettingData(res.data);
      }
    });
  };

  useEffect(() => {
    setAlarmData();
    getNoShowProcessingTime();
  }, []);

  return (
    <Container
      style={{
        minWidth: "1140px",
        boxSizing: "border-box",
      }}
    >
      <UI.Typography
        type="title03"
        fontStyle="bold"
        color="var(--alias-color-label-neutral)"
        style={{ padding: "24px 20px" }}
      >
        알림 정책 설정
      </UI.Typography>
      <SettingContainer>
        {alarmSettingData && (
          <TodayRoomNotificationSetting
            time={alarmSettingData.usageConfirmAlarmTime}
            isActive={alarmSettingData.isUsageConfirmAlarmEnabled}
            alarmSender={alarmSettingData.usageConfirmAlarmTarget}
          />
        )}
        <NoshowNotificationSetting
          hasMeetingRoomUsedTablet={hasMeetingRoomUsedTablet}
          noShowEnabled={noShowEnabled}
          noShowWarningActive={noShowWarningActive}
          onChangeNoShowWarningActive={toggleNoShowWarning}
          noShowWarningTime={noShowWarningTime}
          onChangeWarningTime={selectNoShowWarningTime}
          noShowProcessingActive={noShowProcessingActive}
          onChangeNoshowProcessingAlarmActive={toggleNoShowProcessing}
        />
        <PreMeetingNotificationSetting
          meetingReminderActive={meetingReminderActive}
          onChangeMeetingReminderActive={toggleMeetingReminder}
          meetingReminderTime={meetingReminderTime}
          onChangeMeetingReminderTime={selectMeetingReminderTime}
        />
      </SettingContainer>
    </Container>
  );
};

export default SettingAlarm;
